/**
 * The merged per-tenant schema: the compiled registries plus the tenant's active app manifests, as one registry.
 *
 * `buildTenantRegistry` returns two things. `nodes` and `edges` are full NodeDefinition / EdgeDefinition records (with a
 * zod schema generated from each app type's fields) for server-side validation and agent docs. `schema` is the same
 * registry without the zod schemas, which GET /schema returns and the CRM overlays onto its registry lookups.
 */
import { z } from 'zod';
import type { FormFieldConfig, NodeDefinition, RelationshipSectionConfig } from '../graph/nodes/base-crm';
import type { EdgeDefinition, EdgePropConfig } from '../graph/edges/base-crm';
import { NODE_REGISTRY } from '../graph/nodes';
import { EDGE_REGISTRY } from '../graph/edges';
import { isManagedAppType, type AppField, type AppManifest, type AppObjectType, type AppView } from './manifest';

export type SchemaSource = 'builtin' | 'app';

/** A node type as the CRM sees it: a NodeDefinition without its zod schema, tagged with where it came from. */
export interface SchemaNodeType extends Omit<NodeDefinition, 'schema'> {
  source: SchemaSource;
  appKey?: string;
  /** True when an app redefined this built-in type for the tenant: the key is the built-in's, the definition the app's. */
  overridesBuiltin?: boolean;
}

export interface SchemaEdgeType extends Omit<EdgeDefinition, 'schema'> {
  source: SchemaSource;
  appKey?: string;
}

export interface SchemaView extends AppView {
  appKey: string;
}

export interface SchemaNavItem {
  path: string;
  label: string;
  icon: string;
  objectType: string;
  viewKey: string;
  kind: AppView['kind'];
}

export interface SchemaNavCategory {
  id: string;
  label: string;
  order: number;
  appKey: string;
  items: SchemaNavItem[];
}

export interface InstalledApp {
  id: string;
  key: string;
  name: string;
  version: number;
}

export interface TenantSchema {
  nodeTypes: Record<string, SchemaNodeType>;
  edgeTypes: Record<string, SchemaEdgeType>;
  views: SchemaView[];
  nav: SchemaNavCategory[];
  apps: InstalledApp[];
}

/** An active tenant_apps row, as the schema service loads it. */
export interface InstalledManifest {
  id: string;
  key: string;
  version: number;
  manifest: AppManifest;
}

export interface TenantRegistry {
  nodes: Record<string, NodeDefinition>;
  edges: Record<string, EdgeDefinition>;
  schema: TenantSchema;
}

const GeoSchema = z.object({
  street: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  postal_code: z.string().optional(),
  country: z.string().optional(),
  latitude: z.number().optional(),
  longitude: z.number().optional(),
});

/** The zod type for one field's value; lookup fields are edges, not props, so they have none. */
export function fieldToZod(field: AppField): z.ZodTypeAny | null {
  // A dotted key is a list column filled from an include, not a prop
  if (field.key.includes('.')) return null;
  let base: z.ZodTypeAny;
  switch (field.type) {
    case 'text':
    case 'textarea':
    case 'richtext':
    case 'tel':
      base = field.required ? z.string().min(1, `${field.label} is required`) : z.string();
      break;
    case 'email':
      base = z.union([z.string().email('Invalid email'), z.literal('')]);
      break;
    case 'url':
      base = z.union([z.string().url('Invalid URL'), z.literal('')]);
      break;
    case 'number':
      base = z.number();
      break;
    case 'date':
      base = z.string().date();
      break;
    case 'datetime':
      base = z.string().datetime({ offset: true });
      break;
    case 'select':
      base = field.options && field.options.length > 0 ? z.enum(field.options as [string, ...string[]]) : z.string().min(1);
      break;
    case 'json':
      base = z.union([z.record(z.unknown()), z.array(z.unknown())]);
      break;
    case 'geo':
      base = GeoSchema;
      break;
    case 'user':
      base = z.string().uuid();
      break;
    case 'lookup':
      return null;
  }
  return field.required ? base : base.optional();
}

/** The props schema of an app object type, one key per non-lookup field. */
export function objectTypeToZod(objectType: AppObjectType): z.ZodObject<z.ZodRawShape> {
  const shape: z.ZodRawShape = {};
  for (const field of objectType.fields) {
    const fieldSchema = fieldToZod(field);
    if (fieldSchema) shape[field.key] = fieldSchema;
  }
  return z.object(shape);
}

function toFormField(field: AppField): FormFieldConfig {
  const formField: FormFieldConfig = { key: field.key, label: field.label, type: field.type };
  if (field.required !== undefined) formField.required = field.required;
  if (field.options) formField.options = field.options;
  if (field.picklistName) formField.picklistName = field.picklistName;
  if (field.format) formField.format = field.format;
  if (field.lookupConfig) formField.lookupConfig = field.lookupConfig;
  if (field.newLine !== undefined) formField.newLine = field.newLine;
  return formField;
}

/**
 * The NodeDefinition for a manifest type. When it redefines a built-in type, the built-in's capability flags, synonyms
 * and list includes carry over; everything the manifest states (fields, sections, icon, colour, names) is the app's.
 */
function toNodeDefinition(objectType: AppObjectType, builtin?: NodeDefinition): NodeDefinition {
  const definition: NodeDefinition = {
    type: objectType.key,
    schema: objectTypeToZod(objectType),
    displayName: objectType.displayName,
    displayNamePlural: objectType.displayNamePlural,
    icon: objectType.icon,
    color: objectType.color,
    formFields: objectType.fields.map(toFormField),
    pmnEnabled: builtin?.pmnEnabled ?? false,
  };
  if (builtin?.hasAttachments) definition.hasAttachments = true;
  if (builtin?.hasThreads) definition.hasThreads = true;
  if (builtin?.hasOrgChart) definition.hasOrgChart = true;
  if (builtin?.synonyms) definition.synonyms = builtin.synonyms;
  if (builtin?.listViewIncludes) definition.listViewIncludes = builtin.listViewIncludes;
  if (objectType.description) definition.description = objectType.description;
  if (objectType.naturalKeyField) definition.naturalKeyField = objectType.naturalKeyField;
  if (objectType.readOnly) definition.readOnly = true;
  if (objectType.relationshipSections) definition.relationshipSections = objectType.relationshipSections as RelationshipSectionConfig[];
  return definition;
}

function toEdgeDefinition(edgeType: AppManifest['edgeTypes'][number]): EdgeDefinition {
  const shape: z.ZodRawShape = {};
  for (const prop of edgeType.props ?? []) {
    switch (prop.type) {
      case 'number':
        shape[prop.key] = z.number().optional();
        break;
      case 'boolean':
        shape[prop.key] = z.boolean().optional();
        break;
      case 'date':
        shape[prop.key] = z.string().date().optional();
        break;
      case 'select':
        shape[prop.key] = prop.options && prop.options.length > 0 ? z.enum(prop.options as [string, ...string[]]).optional() : z.string().optional();
        break;
      case 'text':
      case 'picklist':
        shape[prop.key] = z.string().optional();
        break;
    }
  }
  const definition: EdgeDefinition = {
    type: edgeType.key,
    schema: z.object(shape),
    displayName: edgeType.displayName,
    sourceTypes: edgeType.sourceTypes,
    targetTypes: edgeType.targetTypes,
    props: (edgeType.props ?? []) as EdgePropConfig[],
  };
  if (edgeType.reverseDisplayName) definition.reverseDisplayName = edgeType.reverseDisplayName;
  return definition;
}

function withoutSchema<T extends { schema: unknown }>(definition: T): Omit<T, 'schema'> {
  const { schema: _schema, ...rest } = definition;
  return rest;
}

export interface BuiltinDefinitions {
  nodes: Record<string, NodeDefinition>;
  edges: Record<string, EdgeDefinition>;
}

/**
 * Merges the built-in registries with the given manifests. Manifests are applied in the order given (the service
 * passes them oldest install first), and the validator has already refused keys that collide with built-ins, so a
 * later manifest can never replace an earlier type: a duplicate type key or nav path between two manifests is an error
 * here, which the schema service turns into a refused draft.
 */
export function buildTenantRegistry(manifests: readonly InstalledManifest[], builtins: BuiltinDefinitions = { nodes: NODE_REGISTRY, edges: EDGE_REGISTRY }): TenantRegistry {
  const nodes: Record<string, NodeDefinition> = { ...builtins.nodes };
  const edges: Record<string, EdgeDefinition> = { ...builtins.edges };
  const nodeTypes: Record<string, SchemaNodeType> = {};
  const edgeTypes: Record<string, SchemaEdgeType> = {};
  for (const [key, definition] of Object.entries(builtins.nodes)) nodeTypes[key] = { ...withoutSchema(definition), source: 'builtin' };
  for (const [key, definition] of Object.entries(builtins.edges)) edgeTypes[key] = { ...withoutSchema(definition), source: 'builtin' };

  const views: SchemaView[] = [];
  const nav: SchemaNavCategory[] = [];
  const apps: InstalledApp[] = [];
  const navPaths = new Map<string, string>();
  /** Built-in types an app has redefined so far, by app, so two apps never redefine the same one. */
  const overridden = new Map<string, string>();

  for (const installed of manifests) {
    const { manifest } = installed;
    for (const objectType of manifest.objectTypes) {
      const builtin = builtins.nodes[objectType.key];
      if (!builtin && objectType.key in nodes) throw new Error(`node type "${objectType.key}" is already defined; app "${manifest.key}" cannot redefine it`);
      if (builtin) {
        const owner = overridden.get(objectType.key);
        if (owner) throw new Error(`built-in type "${objectType.key}" is already redefined by app "${owner}"; app "${manifest.key}" cannot redefine it too`);
        overridden.set(objectType.key, manifest.key);
      }
      const definition = toNodeDefinition(objectType, builtin);
      nodes[objectType.key] = definition;
      nodeTypes[objectType.key] = { ...withoutSchema(definition), source: 'app', appKey: manifest.key, ...(builtin ? { overridesBuiltin: true } : {}) };
    }
    for (const edgeType of manifest.edgeTypes) {
      if (edgeType.key in edges) throw new Error(`edge type "${edgeType.key}" is already defined; app "${manifest.key}" cannot redefine it`);
      const definition = toEdgeDefinition(edgeType);
      edges[edgeType.key] = definition;
      edgeTypes[edgeType.key] = { ...withoutSchema(definition), source: 'app', appKey: manifest.key };
    }
    for (const item of manifest.nav.items) {
      const owner = navPaths.get(item.path);
      if (owner) throw new Error(`nav path "${item.path}" is already used by app "${owner}"; app "${manifest.key}" cannot take it`);
      navPaths.set(item.path, manifest.key);
    }
    const viewKinds = new Map(manifest.views.map((view) => [view.key, view.kind]));
    for (const view of manifest.views) views.push({ ...view, appKey: manifest.key });
    nav.push({
      id: manifest.nav.category.id,
      label: manifest.nav.category.label,
      order: manifest.nav.category.order ?? 100,
      appKey: manifest.key,
      items: manifest.nav.items.map((item) => ({ ...item, kind: viewKinds.get(item.viewKey) ?? 'table' })),
    });
    apps.push({ id: installed.id, key: manifest.key, name: manifest.name, version: installed.version });
  }

  nav.sort((a, b) => a.order - b.order || a.label.localeCompare(b.label));
  return { nodes, edges, schema: { nodeTypes, edgeTypes, views, nav, apps } };
}

/**
 * Why a record of this type may not be written, or null: a read-only app type is kept by Diggens itself, and so is any
 * type under a key Diggens keeps that the tenant has no writable definition of, so its records stay unwritable while
 * the app is disabled. A tenant's own app that took the key before Diggens did defines its types without readOnly, and
 * stays writable while it is installed.
 */
export function readOnlyRefusal(registry: Pick<TenantRegistry, 'nodes'>, type: string): string | null {
  const definition = registry.nodes[type];
  if (definition) return definition.readOnly ? `${definition.displayNamePlural} are kept by Diggens itself and cannot be created, edited or deleted.` : null;
  return isManagedAppType(type) ? 'These records are kept by Diggens itself and cannot be created, edited or deleted.' : null;
}

/** Where an app key starts in a type key (`app:`). */
const APP_PREFIX_LENGTH = 'app:'.length;

/**
 * Why an edge of this type may not be added, changed or removed, or null: an edge type under a key Diggens keeps is its
 * sync's alone (unless the installed app is the tenant's own, with no read-only types). It reads the type only, so it
 * costs no query; other links to a kept record are allowed, and the sync owns only its own edge types.
 */
export function edgeRefusal(registry: Pick<TenantRegistry, 'nodes' | 'edges'>, edgeType: string): string | null {
  if (!isManagedAppType(edgeType)) return null;
  const appPrefix = edgeType.slice(0, edgeType.indexOf(':', APP_PREFIX_LENGTH) + 1);
  const keptByDiggens = Object.values(registry.nodes).some((definition) => definition.readOnly === true && definition.type.startsWith(appPrefix));
  return keptByDiggens || !registry.edges[edgeType] ? 'These links are kept by Diggens itself and cannot be created, changed or removed.' : null;
}

/**
 * The list path for a node type in a merged schema: the first nav item whose table view shows every record of it, or
 * none. A table with fixed filters (overdue tasks) is skipped, since a record linked from elsewhere may not be in it.
 */
export function findListPath(schema: Pick<TenantSchema, 'nav' | 'views'>, nodeType: string): string | undefined {
  for (const category of schema.nav) {
    for (const item of category.items) {
      if (item.objectType !== nodeType || item.kind !== 'table') continue;
      const view = schema.views.find((candidate) => candidate.appKey === category.appKey && candidate.key === item.viewKey);
      if (!view?.filters || view.filters.length === 0) return item.path;
    }
  }
  return undefined;
}
